// docs/110 SE6 — the moment a player puts on the last piece of a set: their screen (and only theirs;
// nothing goes to the server or to anyone else) is taken over by the set's fanfare. A WebGL shader
// throws light in the set's two colours (god rays turning round a burst, a ring that rolls out,
// a vignette), a canvas throws the set's particles, the stinger and the set's own sound play, and a
// card says which set is whole and what it now grants. Click, Enter, Space or Escape (or 9 s) ends it.
//
// `SetFanfareHost` (mounted once in App) watches the World's item rows for my own heroes and starts
// the fanfare when a hero of mine goes from wearing part of a set to all of it. `celebrateSet` starts
// one directly (the `?sets=1` book, tests).
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { create } from 'zustand';
import { useWorldHeroes, useWorldItems } from '../../core/subscriptions/hooks';
import { useSession } from '../../core/stores/session';
import { identityHex } from '../../core/net/connection';
import { completeSets, itemSet, type ItemSetSpec } from '../../core/items/catalogue';
import { describeSet } from '../../core/items/describe';
import { isSfxKey } from '../../core/items/sound';
import { playSfx } from '../../core/audio';
import { useReducedMotion } from '../components/Motion';
import { ItemIcon } from '../inventory/ItemCard';
import './setFanfare.css';

interface Fanfare { key: number; set: ItemSetSpec; hero: string }
interface FanfareStore {
  queue: Fanfare[];
  push(set: ItemSetSpec, hero: string): void;
  shift(): void;
}
let seq = 0;
export const useSetFanfare = create<FanfareStore>()((set) => ({
  queue: [],
  push: (s, hero) => set((st) => ({ queue: [...st.queue, { key: ++seq, set: s, hero }] })),
  shift: () => set((st) => ({ queue: st.queue.slice(1) })),
}));

/** Starts the fanfare for a set on this screen. */
export function celebrateSet(setId: number, hero: string): boolean {
  const s = itemSet(setId);
  if (!s) return false;
  useSetFanfare.getState().push(s, hero);
  return true;
}

/** The ids of the sets worn whole among these worn defs. */
export const wholeSets = (worn: Iterable<number>): Set<number> => new Set(completeSets(worn).map((s) => s.id));

/** The sets to celebrate: whole now and not before. A hero seen for the first time celebrates nothing. */
export function newlyWhole(before: ReadonlySet<number> | undefined, now: ReadonlySet<number>): number[] {
  if (!before) return [];
  return [...now].filter((id) => !before.has(id));
}

/** How long after a World's rows first arrive before a change counts as the player's doing. */
const SETTLE_MS = 2500;

/**
 * Watches my heroes' worn pieces in the current World and starts a fanfare for each set that has
 * just become whole. The first look at each hero only records what they already wear, so loading a
 * World with sets already on never celebrates them again.
 */
function SetWatcher({ worldId }: { worldId: bigint }) {
  const me = useSession((s) => s.identity ?? '');
  const heroes = useWorldHeroes(worldId);
  const items = useWorldItems(worldId);
  const seen = useRef(new Map<string, Set<number>>());
  const since = useRef(0);
  useEffect(() => { seen.current = new Map(); since.current = 0; }, [worldId]);
  useEffect(() => {
    if (!items.length && !since.current) return;
    if (!since.current) since.current = performance.now();
    const settled = performance.now() - since.current > SETTLE_MS;
    for (const h of heroes) {
      if (identityHex(h.owner) !== me) continue;
      const key = h.id.toString();
      const whole = wholeSets(items.filter((i) => i.heroId === h.id && i.equippedSlot !== undefined && i.equippedSlot !== null).map((i) => i.def));
      const before = seen.current.get(key);
      seen.current.set(key, whole);
      if (!settled) continue;
      for (const id of newlyWhole(before, whole)) celebrateSet(id, h.name);
    }
  }, [heroes, items, me]);
  return null;
}

export function SetFanfareHost() {
  const worldId = useSession((s) => s.worldId);
  const head = useSetFanfare((s) => s.queue[0]);
  const shift = useSetFanfare((s) => s.shift);
  useEffect(() => {
    (window as unknown as { __fkSetFanfare?: typeof celebrateSet }).__fkSetFanfare = celebrateSet;
  }, []);
  return (
    <>
      {worldId !== undefined && <SetWatcher worldId={worldId} />}
      {head && <SetFanfareView key={head.key} set={head.set} hero={head.hero} onDone={shift} />}
    </>
  );
}

const hexRgb = (h: string): [number, number, number] => {
  const n = parseInt(h.slice(1), 16);
  return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255];
};

/** How long the full show runs before it leaves by itself. */
const HOLD_MS = 9000;

export function SetFanfareView({ set, hero, onDone, hold = HOLD_MS }: { set: ItemSetSpec; hero: string; onDone(): void; hold?: number }) {
  const reduced = useReducedMotion();
  const glRef = useRef<HTMLCanvasElement>(null);
  const fxRef = useRef<HTMLCanvasElement>(null);
  const [leaving, setLeaving] = useState(false);
  const words = useMemo(() => describeSet(set), [set]);
  const f = set.fanfare;
  const leave = useRef(() => {});
  leave.current = () => {
    if (leaving) return;
    setLeaving(true);
    window.setTimeout(onDone, reduced ? 0 : 420);
  };

  // the sound: the shared stinger, the set's own sound over it, and a bright tail
  useEffect(() => {
    playSfx('cue.stinger_triumph', { origin: 'local' });
    const own = window.setTimeout(() => { if (isSfxKey(f.sfx)) playSfx(f.sfx, { origin: 'local', gain: 0.9 }); }, 180);
    const tail = window.setTimeout(() => playSfx('ui.levelup', { origin: 'local', gain: 0.7 }), 900);
    return () => { window.clearTimeout(own); window.clearTimeout(tail); };
  }, [f.sfx]);

  // leave on a key or by itself
  useEffect(() => {
    const key = (e: KeyboardEvent) => {
      if (['Escape', 'Enter', ' '].includes(e.key)) { e.preventDefault(); e.stopPropagation(); leave.current(); }
    };
    window.addEventListener('keydown', key, true);
    const t = window.setTimeout(() => leave.current(), hold);
    return () => { window.removeEventListener('keydown', key, true); window.clearTimeout(t); };
  }, [hold]);

  // the shader
  useEffect(() => {
    const c = glRef.current;
    if (!c) return;
    return runShader(c, hexRgb(f.colour), hexRgb(f.accent), reduced);
  }, [f.colour, f.accent, reduced]);

  // the particles
  useEffect(() => {
    const c = fxRef.current;
    if (!c) return;
    return runParticles(c, f.particles ?? 'sparks', f.colour, f.accent, reduced ? 40 : 220);
  }, [f.particles, f.colour, f.accent, reduced]);

  const style = { '--fan-c': f.colour, '--fan-a': f.accent } as React.CSSProperties;
  return createPortal(
    <div className={`fk-fanfare ${leaving ? 'is-leaving' : ''} ${reduced ? 'is-still' : ''}`} style={style} role="dialog" aria-modal="true" aria-labelledby="fk-fanfare-title"
      onClick={() => leave.current()} data-testid="set-fanfare" data-set={set.id}>
      <canvas ref={glRef} className="fk-fanfare__gl" aria-hidden="true" />
      <canvas ref={fxRef} className="fk-fanfare__fx" aria-hidden="true" />
      <div className="fk-fanfare__card" onClick={(e) => e.stopPropagation()}>
        <div className="fk-fanfare__eyebrow">Full set equipped</div>
        <h2 className="fk-fanfare__title" id="fk-fanfare-title">{f.title}</h2>
        <p className="fk-fanfare__who"><b>{hero}</b> now wears the whole <b>{set.name}</b>.</p>
        <ul className="fk-fanfare__pieces" aria-label="The pieces">
          {set.members.map((m, i) => <li key={m} style={{ '--i': i } as React.CSSProperties}><ItemIcon def={m} scale={2} framed /></li>)}
        </ul>
        <div className="fk-fanfare__bonus" data-testid="set-fanfare-bonus">
          <div className="fk-fanfare__bonus-head">The set bonus is yours</div>
          <ul>{words.bonus.map((l) => <li key={l}>{l}</li>)}</ul>
          <div className="fk-fanfare__ability"><span className="fk-fanfare__tag">New ability</span> <b>{words.ability.name}</b> <span className="fk-fanfare__ability-head">{words.ability.head}</span>
            <div className="fk-fanfare__ability-line">{words.ability.effects.join('; ')}</div>
          </div>
        </div>
        <p className="fk-fanfare__flavour">“{set.flavour}”</p>
        <button type="button" className="fk-fanfare__ok" onClick={() => leave.current()} data-testid="set-fanfare-ok" autoFocus>Onward!</button>
      </div>
    </div>,
    document.body,
  );
}

/* ---------- the shader: rays round a burst, a rolling ring, a vignette ---------- */

const VERT = `attribute vec2 p; void main() { gl_Position = vec4(p, 0.0, 1.0); }`;
const FRAG = `precision mediump float;
uniform vec2 res; uniform float t; uniform vec3 c1; uniform vec3 c2;
float hash(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
void main() {
  vec2 uv = (gl_FragCoord.xy - 0.5 * res) / res.y;
  float r = length(uv); float a = atan(uv.y, uv.x);
  float intro = smoothstep(0.0, 0.6, t);
  // turning god rays, two sets at different speeds
  float rays = pow(0.5 + 0.5 * sin(a * 12.0 + t * 0.9), 3.0) * 1.1 + pow(0.5 + 0.5 * sin(a * 7.0 - t * 0.6 + 1.3), 5.0) * 0.8;
  rays *= smoothstep(1.3, 0.08, r) * smoothstep(0.0, 0.12, r) * intro;
  // the burst at the heart, flaring once and then breathing
  float flare = exp(-t * 2.2) * 2.5 + 0.35 + 0.08 * sin(t * 3.0);
  float core = flare * exp(-r * r * 22.0);
  // a ring that rolls out on the first beat, and a softer second one
  float ring = exp(-pow((r - t * 0.9) * 14.0, 2.0)) * exp(-t * 0.9) * 1.6;
  float ring2 = exp(-pow((r - max(t - 0.35, 0.0) * 0.65) * 18.0, 2.0)) * exp(-t * 1.1);
  // a little sparkle in the rays
  float sp = step(0.996, hash(floor(gl_FragCoord.xy / 3.0) + floor(t * 8.0))) * smoothstep(0.9, 0.1, r) * intro;
  vec3 col = c1 * (rays + core + ring) + c2 * (ring2 * 1.2 + rays * 0.35) + vec3(1.0) * (core * 0.35 + sp);
  float vig = smoothstep(1.25, 0.25, r);
  float alpha = clamp((rays + core + ring + ring2) * 0.75 + 0.55 * intro, 0.0, 0.92) ;
  vec3 back = mix(c2 * 0.25, vec3(0.02, 0.03, 0.07), 0.55);
  gl_FragColor = vec4(mix(back, col, clamp(rays + core + ring + ring2 + sp, 0.0, 1.0)) * (0.55 + 0.45 * vig), alpha);
}`;

function runShader(canvas: HTMLCanvasElement, c1: number[], c2: number[], still: boolean): () => void {
  const gl = canvas.getContext('webgl', { premultipliedAlpha: false, alpha: true });
  if (!gl) { console.warn('[fanfare] no webgl'); canvas.classList.add('is-fallback'); return () => {}; }
  const sh = (type: number, src: string) => { const s = gl.createShader(type)!; gl.shaderSource(s, src); gl.compileShader(s); return s; };
  const prog = gl.createProgram()!;
  gl.attachShader(prog, sh(gl.VERTEX_SHADER, VERT));
  gl.attachShader(prog, sh(gl.FRAGMENT_SHADER, FRAG));
  gl.linkProgram(prog);
  if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) {
    console.warn('[fanfare] shader', gl.getProgramInfoLog(prog));
    canvas.classList.add('is-fallback');
    return () => {};
  }
  gl.useProgram(prog);
  const buf = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, buf);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
  const loc = gl.getAttribLocation(prog, 'p');
  gl.enableVertexAttribArray(loc);
  gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
  const uRes = gl.getUniformLocation(prog, 'res'), uT = gl.getUniformLocation(prog, 't');
  gl.uniform3fv(gl.getUniformLocation(prog, 'c1'), c1);
  gl.uniform3fv(gl.getUniformLocation(prog, 'c2'), c2);
  gl.enable(gl.BLEND);
  gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
  const t0 = performance.now();
  let raf = 0;
  const frame = () => {
    // half resolution: the light is soft, and the takeover should cost little
    const dpr = Math.min(window.devicePixelRatio || 1, 2) * 0.5;
    const w = Math.max(1, Math.round(canvas.clientWidth * dpr)), h = Math.max(1, Math.round(canvas.clientHeight * dpr));
    if (canvas.width !== w || canvas.height !== h) { canvas.width = w; canvas.height = h; }
    gl.viewport(0, 0, w, h);
    gl.uniform2f(uRes, w, h);
    gl.uniform1f(uT, still ? 1.6 : (performance.now() - t0) / 1000);
    gl.clearColor(0, 0, 0, 0);
    gl.clear(gl.COLOR_BUFFER_BIT);
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
    if (!still) raf = requestAnimationFrame(frame);
  };
  frame();
  // the context goes with its canvas (losing it by hand would break StrictMode's second run, which
  // gets the same context back from the same canvas)
  return () => { cancelAnimationFrame(raf); gl.deleteProgram(prog); gl.deleteBuffer(buf); };
}

/* ---------- the particles ---------- */

interface Bit { x: number; y: number; vx: number; vy: number; life: number; age: number; size: number; spin: number; rot: number; col: string }

/** How each kind of particle moves: gravity (+ falls, − rises), drag, and how it is thrown. */
const MOTION: Record<string, { g: number; drag: number; speed: number; rain?: boolean }> = {
  embers: { g: -40, drag: 0.98, speed: 420 },
  sparks: { g: 260, drag: 0.97, speed: 620 },
  snow: { g: 30, drag: 0.99, speed: 120, rain: true },
  petals: { g: 45, drag: 0.985, speed: 300 },
  motes: { g: -20, drag: 0.97, speed: 260 },
  bubbles: { g: -70, drag: 0.98, speed: 220 },
  leaves: { g: 60, drag: 0.98, speed: 340 },
  stars: { g: 0, drag: 0.965, speed: 520 },
  coins: { g: 520, drag: 0.99, speed: 640 },
  shards: { g: 380, drag: 0.98, speed: 700 },
};

function runParticles(canvas: HTMLCanvasElement, kind: string, c1: string, c2: string, count: number): () => void {
  const ctx = canvas.getContext('2d');
  if (!ctx) return () => {};
  const m = MOTION[kind] ?? MOTION.sparks!;
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  const fit = () => { canvas.width = canvas.clientWidth * dpr; canvas.height = canvas.clientHeight * dpr; };
  fit();
  const W = () => canvas.width / dpr, H = () => canvas.height / dpr;
  const bits: Bit[] = [];
  const cols = [c1, c2, '#ffffff', c1];
  const spawn = (n: number, wave: number) => {
    for (let i = 0; i < n; i++) {
      const a = Math.random() * Math.PI * 2;
      const s = m.speed * (0.35 + Math.random() * 0.75) * (wave ? 0.7 : 1);
      const fromTop = m.rain && Math.random() < 0.6;
      bits.push({
        x: fromTop ? Math.random() * W() : W() / 2, y: fromTop ? -10 : H() / 2,
        vx: fromTop ? (Math.random() - 0.5) * 60 : Math.cos(a) * s, vy: fromTop ? 40 + Math.random() * 80 : Math.sin(a) * s - (kind === 'coins' ? 260 : 0),
        life: 1.6 + Math.random() * 2.2, age: 0, size: 2 + Math.random() * (kind === 'petals' || kind === 'leaves' ? 6 : 4),
        spin: (Math.random() - 0.5) * 8, rot: Math.random() * 6.28, col: cols[i % cols.length]!,
      });
    }
  };
  spawn(count, 0);
  const second = window.setTimeout(() => spawn(Math.round(count * 0.5), 1), 450);
  let last = performance.now(); let raf = 0;
  const draw = (b: Bit) => {
    ctx.save();
    ctx.translate(b.x, b.y); ctx.rotate(b.rot);
    ctx.globalAlpha = Math.max(0, 1 - b.age / b.life);
    ctx.fillStyle = b.col; ctx.strokeStyle = b.col;
    const s = b.size;
    switch (kind) {
      case 'sparks': case 'shards': ctx.fillRect(-s * 1.6, -s * 0.25, s * 3.2, s * 0.5); break;
      case 'snow': case 'motes': case 'embers': ctx.beginPath(); ctx.arc(0, 0, s * 0.6, 0, 6.29); ctx.fill(); break;
      case 'bubbles': ctx.lineWidth = 1.2; ctx.beginPath(); ctx.arc(0, 0, s, 0, 6.29); ctx.stroke(); break;
      case 'petals': case 'leaves': ctx.beginPath(); ctx.ellipse(0, 0, s, s * 0.45, 0, 0, 6.29); ctx.fill(); break;
      case 'coins': ctx.beginPath(); ctx.ellipse(0, 0, s, s * Math.abs(Math.cos(b.rot * 2)) + 0.6, 0, 0, 6.29); ctx.fill(); break;
      default: { // stars
        ctx.beginPath();
        for (let k = 0; k < 10; k++) { const r = k % 2 ? s * 0.45 : s * 1.3; const a = (k / 10) * 6.283; ctx.lineTo(Math.cos(a) * r, Math.sin(a) * r); }
        ctx.closePath(); ctx.fill();
      }
    }
    ctx.restore();
  };
  const tick = (now: number) => {
    const dt = Math.min(0.05, (now - last) / 1000); last = now;
    if (canvas.width !== canvas.clientWidth * dpr) fit();
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, W(), H());
    ctx.globalCompositeOperation = kind === 'embers' || kind === 'sparks' || kind === 'stars' || kind === 'motes' ? 'lighter' : 'source-over';
    for (let i = bits.length - 1; i >= 0; i--) {
      const b = bits[i]!;
      b.age += dt;
      if (b.age > b.life) { bits.splice(i, 1); continue; }
      b.vx *= m.drag; b.vy = b.vy * m.drag + m.g * dt;
      if (kind === 'leaves' || kind === 'petals' || kind === 'snow') b.vx += Math.sin(b.age * 3 + b.rot) * 20 * dt;
      b.x += b.vx * dt; b.y += b.vy * dt; b.rot += b.spin * dt;
      draw(b);
    }
    raf = requestAnimationFrame(tick);
  };
  raf = requestAnimationFrame(tick);
  return () => { cancelAnimationFrame(raf); window.clearTimeout(second); };
}
